import React from "react";
import {
  Box,
  Typography,
  Link as MuiLink,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScaleIcon from "@mui/icons-material/Scale";
import LinkIcon from "@mui/icons-material/Link";
import { getSportsIcon } from "../../constants";
import ToggleableDate from "../ToggleableDate";
import moment from "moment";
import { Link } from "react-router-dom";

const SGMHeader = ({
  eventName,
  eventDateTime,
  eventData,
  handleBack,
  triggerRefresh,
  loading,
  anchorEl,
  handleRelatedClick,
  handleRelatedClose,
  handleRelatedSelect,
  onOpenBookieWeightsModal,
  hasCustomBookieWeights,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Format the related events menu items
  const relatedEvents =
    eventData && eventData.relatedEvents ? eventData.relatedEvents : [];
  const hasRelatedEvents = relatedEvents.length > 0;

  // Get the sport from event data
  const sport = eventData?.sport;
  const sportIcon = getSportsIcon(sport);

  // Format the date time for display
  let formattedDateTime = "";
  if (eventDateTime) {
    const eventDate = moment(eventDateTime);

    if (moment().isSame(eventDate, "day")) {
      formattedDateTime = `Today ${eventDate.format("h:mm A")}`;
    } else if (moment().add(1, "day").isSame(eventDate, "day")) {
      formattedDateTime = `Tomorrow ${eventDate.format("h:mm A")}`;
    } else if (moment().add(7, "day").isAfter(eventDate)) {
      formattedDateTime = `${eventDate.format("dddd")} ${eventDate.format(
        "h:mm A"
      )}`;
    } else {
      formattedDateTime = `${eventDate.format(
        "dddd, MMMM Do"
      )} ${eventDate.format("h:mm A")}`;
    }
  }

  const relatedEventsCount = eventData?.relatedEvents?.length || 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mb: 3,
        gap: 2,
      }}
    >
      {/* Top row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Back button */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleBack}
            sx={{ mr: 1, color: theme.palette.primary.main }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {eventName || "Loading event..."}
          </Typography>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Event link button */}
          <Tooltip title="View event details page">
            <Button
              component={Link}
              to={eventData?.eventId ? `/event/${eventData.eventId}` : "#"}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              startIcon={<LinkIcon />}
              sx={{ whiteSpace: "nowrap" }}
              disabled={!eventData?.eventId}
            >
              Event Details
            </Button>
          </Tooltip>

          {/* Bookie Weights Button */}
          <Tooltip title="Customize bookie weights for EV calculations">
            <Badge
              color="primary"
              variant="dot"
              invisible={!hasCustomBookieWeights}
            >
              <Button
                onClick={onOpenBookieWeightsModal}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                startIcon={<ScaleIcon />}
                sx={{
                  whiteSpace: "nowrap",
                }}
              >
                Bookie Weights
              </Button>
            </Badge>
          </Tooltip>

          {/* Refresh button */}
          <Tooltip title="Refresh data">
            <IconButton
              onClick={triggerRefresh}
              disabled={loading}
              sx={{ color: theme.palette.primary.main }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Date and related events row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Event date and sport */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ...(isMobile && {
              width: "100%",
              mb: 1,
            }),
          }}
        >
          {sportIcon && (
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
              }}
            >
              {sportIcon}
            </Box>
          )}
          <ToggleableDate
            date={eventDateTime}
            fullWidth={isMobile}
            showTime
            color="textSecondary"
          />
        </Box>

        {/* Related events dropdown */}
        {hasRelatedEvents && (
          <Box>
            <Button
              variant="outlined"
              endIcon={<ExpandMoreIcon />}
              onClick={handleRelatedClick}
              size={isMobile ? "small" : "medium"}
              sx={{
                whiteSpace: "nowrap",
                ...(isMobile && {
                  width: "100%",
                  marginTop: 1,
                }),
              }}
            >
              Related Events ({relatedEvents.length})
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleRelatedClose}
              MenuListProps={{
                "aria-labelledby": "related-events-button",
              }}
              sx={{
                maxWidth: isMobile ? "calc(100% - 32px)" : "auto",
              }}
            >
              {relatedEvents.map((event) => (
                <MenuItem
                  key={event.id}
                  onClick={() => handleRelatedSelect(event.id)}
                  sx={{
                    bgcolor:
                      event.id === eventData?.eventId
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <Box sx={{ mr: 1 }}>{event.name}</Box>
                  {event.id === eventData?.eventId && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SGMHeader;
